import { Outlet, useLocation } from 'react-router-dom';
import { Box, Button, Center, Flex, IconButton, Spinner, Stack } from '@chakra-ui/react';
import { Navbar } from './navbar';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ArrowRightFromLine, ArrowLeftFromLine } from 'lucide-react';
import { Suspense } from 'react';
import { SideBarNavButtons } from './SidebarAdmin';
import { getAdminTitle } from './pageTitle';
import { useIsWiderThan940px } from '@duke-hq/ui/components/hooks/useIsWiderThan940px';
import { useNavButton } from '@duke-hq/ui/components/MainSidebar/useNavButton';
import { Helmet } from 'react-helmet-async';
import { sizes, sizeVars } from '@duke-hq/ui/constants/layout';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export function LayoutAdmin() {
  const location = useLocation();
  const [isLargerThan940] = useIsWiderThan940px();

  // Every editor now renders its own secondary toolbar (EditorToolbar) beneath
  // this primary header, so the primary header is always the plain Navbar.
  const navbar = <Navbar />;

  const { expanded, toggleExpanded } = useNavButton();

  const permanentSidebar = (
    <Flex
      as="section"
      pos="sticky"
      top={sizeVars.navbar}
      minH={`calc(100vh - ${sizeVars.navbar})`}
      flexShrink={0}
      maxW={24}
      justify={'center'}
      w={expanded && isLargerThan940 ? sizes.sidebarExpanded : sizes.sidebarCollapsed}
      minW={expanded && isLargerThan940 ? sizes.sidebarExpanded : sizes.sidebarCollapsed}
      bg="bg.subtle"
      borderRightWidth="1px"
      p="2"
    >
      <Stack
        alignItems={expanded ? 'flex-start' : 'center'}
        justify="space-between"
        spacing="3"
        width="full"
      >
        <Stack spacing="3" w="full" alignItems={expanded ? 'flex-start' : 'center'}>
          <SideBarNavButtons />
        </Stack>
        {expanded ? (
          <Button
            mt="auto"
            variant="tertiary"
            justifyContent="start"
            width={'full'}
            px="2"
            onClick={toggleExpanded}
            leftIcon={<ArrowLeftFromLine size={iconSize.md} />}
            hidden={!isLargerThan940}
          >
            Collapse
          </Button>
        ) : (
          <IconButton
            variant="tertiary"
            aria-label="expand sidebar"
            icon={<ArrowRightFromLine size={iconSize.md} />}
            onClick={toggleExpanded}
            hidden={!isLargerThan940}
          />
        )}
      </Stack>
    </Flex>
  );

  const currentPage = location.pathname.split('/')[1];
  let title = getAdminTitle(currentPage);
  title += 'Admin ~ Duke';

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Box pos="sticky" top={0} zIndex={1012}>
        {navbar}
      </Box>

      <Flex w={'100%'} minH={`calc(100vh - ${sizeVars.navbar})`}>
        {permanentSidebar}

        <ErrorBoundary>
          <Box flex={1} minW={0} overflow="hidden">
            <Suspense
              fallback={
                <Center h={`calc(100vh - ${sizeVars.navbar})`} w="full">
                  <Spinner size="xl" />
                </Center>
              }
            >
              <Outlet />
            </Suspense>
          </Box>
        </ErrorBoundary>
      </Flex>
    </div>
  );
}
